import React from 'react';
import Icon from '../../../components/AppIcon';
import { Field } from './BackgroundFields';
import { getMenuTabOrderEditorEntries } from '../utils/defaults';
import MenuTabEntryAppearanceFields from './MenuTabEntryAppearanceFields';

function tabLabel(entry, sections, links, menuButtons) {
  if (entry.kind === 'section') {
    const sec = sections.find((s) => s.id === entry.id);
    return sec?.label || 'Section';
  }
  if (entry.kind === 'button') {
    const btn = (menuButtons || []).find((b) => b.id === entry.id);
    return btn?.label || 'Bouton';
  }
  const link = links.find((l) => l.id === entry.id);
  return link?.label || 'Lien';
}

function tabBadge(entry) {
  if (entry.kind === 'section') return 'Section';
  if (entry.kind === 'button') return 'Bouton';
  return 'Lien';
}

export default function MenuTabsOrderFields({
  sections,
  menuProps,
  onChangeTabOrder,
  onChangeHiddenMenuSectionIds,
  onChangeTabHoverEffect,
  onChangeTabAppearance,
  onChangeTabPlacement,
}) {
  const links = menuProps?.externalLinks || [];
  const menuButtons = menuProps?.menuButtons || [];
  const entries = getMenuTabOrderEditorEntries(sections, menuProps);
  const hiddenMenu = new Set(menuProps?.hiddenMenuSectionIds || []);
  const freeLayout = menuProps?.menuFreeLayout === true;
  const tabHoverEffects = menuProps?.tabHoverEffects || {};
  const tabAppearances = menuProps?.tabAppearances || {};

  const move = (index, dir) => {
    const keys = entries.map((e) => `${e.kind}:${e.id}`);
    const j = index + dir;
    if (j < 0 || j >= keys.length) return;
    [keys[index], keys[j]] = [keys[j], keys[index]];
    onChangeTabOrder(keys);
  };

  const toggleSectionInMenu = (sectionId) => {
    const next = new Set(hiddenMenu);
    if (next.has(sectionId)) next.delete(sectionId);
    else next.add(sectionId);
    onChangeHiddenMenuSectionIds([...next]);
  };

  if (!entries.length) {
    return (
      <p className="text-xs text-muted-foreground">
        Ajoutez des sections ou des liens externes pour configurer la barre de navigation.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <Field label="Ordre, visibilité et survol (barre du haut)">
        <ul className="space-y-1.5 rounded-lg border border-border divide-y divide-border overflow-hidden">
          {entries.map((entry, index) => {
            const isSection = entry.kind === 'section';
            const isButton = entry.kind === 'button';
            const hidden = isSection && hiddenMenu.has(entry.id);
            const key = `${entry.kind}:${entry.id}`;

            return (
              <li
                key={key}
                className={`px-2 py-2 bg-background text-sm ${hidden ? 'opacity-55' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0 w-14">
                    {tabBadge(entry)}
                  </span>
                  <span
                    className={`flex-1 min-w-0 truncate ${
                      hidden ? 'line-through text-muted-foreground' : 'text-foreground'
                    }`}
                  >
                    {tabLabel(entry, sections, links, menuButtons)}
                  </span>
                  {isSection ? (
                    <button
                      type="button"
                      className={`p-1 rounded hover:bg-muted shrink-0 ${
                        hidden ? 'text-muted-foreground' : 'text-foreground'
                      }`}
                      title={hidden ? 'Afficher dans le menu' : 'Masquer du menu'}
                      onClick={() => toggleSectionInMenu(entry.id)}
                    >
                      <Icon name={hidden ? 'EyeOff' : 'Eye'} size={14} />
                    </button>
                  ) : (
                    <span className="w-[26px] shrink-0" aria-hidden />
                  )}
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-muted disabled:opacity-30 shrink-0"
                    disabled={index === 0}
                    title="Monter"
                    onClick={() => move(index, -1)}
                  >
                    <Icon name="ChevronUp" size={14} />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-muted disabled:opacity-30 shrink-0"
                    disabled={index === entries.length - 1}
                    title="Descendre"
                    onClick={() => move(index, 1)}
                  >
                    <Icon name="ChevronDown" size={14} />
                  </button>
                </div>
                {isButton ? (
                  <p className="mt-2 pl-[4.5rem] text-[11px] text-muted-foreground">
                    Apparence, survol et « épinglé à droite » : section{' '}
                    <strong className="font-medium text-foreground">Boutons du menu</strong>.
                  </p>
                ) : (
                  <div className="mt-2 pl-[4.5rem]">
                    <MenuTabEntryAppearanceFields
                      tabKey={key}
                      menuProps={menuProps}
                      tabHoverEffects={tabHoverEffects}
                      tabAppearances={tabAppearances}
                      onChangeTabHoverEffect={onChangeTabHoverEffect}
                      onChangeTabAppearance={onChangeTabAppearance}
                      onChangeTabPlacement={onChangeTabPlacement}
                      freeLayout={freeLayout}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Field>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        L&apos;icône œil masque une section du menu sans la supprimer de la page. Sur{' '}
        <strong className="font-medium text-foreground">menu hamburger</strong>, l&apos;ordre reste
        celui des sections dans l&apos;éditeur ; seules les sections masquées sont exclues.
        Chaque entrée peut être <strong className="font-medium text-foreground">transparente</strong>{' '}
        ou en <strong className="font-medium text-foreground">bouton plein</strong>, avec{' '}
        <strong className="font-medium text-foreground">ses propres couleurs</strong>, son survol
        et éventuellement <strong className="font-medium text-foreground">épinglée à droite</strong>
        (fond éclairci, soulignement…).
        Sans réglage, un léger assombri ou éclairci du fond s&apos;applique selon la couleur du menu.
        {freeLayout ? (
          <>
            {' '}
            En <strong className="font-medium text-foreground">disposition libre</strong>, glissez
            logo et onglets sur la barre (vue ordinateur).
          </>
        ) : null}
      </p>
    </div>
  );
}
